import { MainBreadcrumbItem } from '../../MainBreadcrumb/MainBreadcrumb';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { SelectedMenuContext } from '../../../contexts/selectedMenu.context';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { fetcher } from '../../../extras/fetcher';
import { ForkliftInspectionInterface } from '../../../types/forkliftInspection.interface';
import { ColumnsType } from 'antd/es/table';
import { myDateSorter, myNumberSorter, myStringSorter } from '../../../extras/sorters';
import { CheckOutlined, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { PermError } from '../NotFoundPage/PermError';
import { Button, DatePicker, Input, InputRef, Space, Table, TableColumnType } from 'antd';
import { TableHeader } from '../Ikea/Stan/TableHeader';
import { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { excelDateFormat, myDateFormat } from '../../../extras/myDateFormat';
import { FileDown } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { ForkliftInterface } from '../../../types/forklift.interface';
import { useParams } from 'react-router-dom';
import { TableFooter } from '../Ikea/Stan/TableFooter';
import { exportToExcel } from '../../../extras/exportToExcel';
import { API_URL } from '../../../config';



interface Props {
  handleSetMainTitle: (title: string) => void;
  setBreadcrumbHandler: (items: MainBreadcrumbItem[] | null) => void;
}


export const ForkliftInspection = (props: Props) => {


  const [loading, setLoading] = useState<boolean>(false);
  const selectedMenuContext = useContext(SelectedMenuContext);
  const { windowHeight } = useWindowDimensions();
  const [permError, setPermError] = useState<boolean>(false);
  const [tableData, setTableData] = useState<ForkliftInspectionInterface[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<InputRef>(null);
  const [searchedColumnKey, setSearchedColumnKey] = useState<string>('');
  const [currentRange, setCurrentRange] = useState<[start: Dayjs | null | undefined, end: Dayjs | null | undefined]>([dayjs(dayjs(), 'DD/MM/YYYY'), dayjs(dayjs(), 'DD/MM/YYYY')]);
  const linkParams =  useParams<{forkliftId: string}>();
  const [forklift, setForklift] = useState<ForkliftInterface|null>(null);
  const [tableSelected, setTableSelected] = useState<ForkliftInspectionInterface[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);


  type DataPath = string | (string | number)[];
  type DataIndexInput = DataPath | DataPath[];


  useEffect(() => {
    if(linkParams.forkliftId) {

      (async() => {
        const getForklift = await fetcher(`forklift/${linkParams.forkliftId}`)
        setForklift(getForklift.result);
      })()


    } else {
      setForklift(null);
    }
  }, [linkParams.forkliftId]);

  const dataIndexKey = (dataIndex: DataIndexInput) => JSON.stringify(dataIndex);

  function getByPath(obj: any, path: DataPath): any {
    if (obj == null) return undefined;

    // "name"
    if (typeof path === "string") return obj[path];

    // ["user","name"] / [0,"x"] itd.
    return path.reduce<any>((acc, key) => (acc == null ? undefined : acc[key]), obj);
  }

  function normalizeDataIndex(input: DataIndexInput): DataPath[] {
    return Array.isArray(input) && (typeof input[0] !== "string" && typeof input[0] !== "number")
      ? (input as DataPath[]) // case: array of paths, but first element is itself an array -> ok
      : (Array.isArray(input) && (typeof input[0] === "string" || typeof input[0] === "number"))
        ? [input as DataPath] // case: single path array ["user","name"]
        : Array.isArray(input)
          ? (input as DataPath[]) // case: ["serialNumber","number"]
          : [input]; // case: "name"
  }

  /**
   * Zwraca tekst do porównywania w filtrze (z wielu pól robi jeden string)
   */
  function recordToSearchText(record: any, dataIndex: DataIndexInput): string {
    const paths = normalizeDataIndex(dataIndex);

    const values = paths
      .map((p) => getByPath(record, p))
      .flatMap((v) => {
        if (v == null) return [];
        if (Array.isArray(v)) return v; // gdy np. pole jest tablicą stringów
        return [v];
      })
      .map((v) => String(v).trim())
      .filter(Boolean);

    return values.join(" ").toLowerCase();
  }


  const getData = async () => {

    setLoading(true);

    if(currentRange?.length !== 2) {
      setLoading(false);
      setTableData([]);
      return;
    }


    if(!currentRange[0] || !currentRange[1]) {
      setTableData([]);
      setLoading(false);
      return;
    }
    const data = await fetcher(`forklift/inspection/${linkParams.forkliftId ?? 'all'}/${currentRange[0].format('YYYY-MM-DD')}/${currentRange[1].format('YYYY-MM-DD')}`);
    if(data.status === 403) {
      setPermError(true);
      return;


    }

    const result: ForkliftInspectionInterface[] = data.result;

    setTableSelected([]);
    setSelectedRowKeys([]);

    setTableData(result.map((d) => {

      return {
        ...d,
        createdAt: myDateFormat(d.createdAt) as any as Date,
        dateForExcel: excelDateFormat(d.createdAt),
      }

    }));
    setLoading(false);


  }

  useEffect(() => {
    getData();
  },[currentRange, linkParams.forkliftId]);


  useEffect(() => {
    const breadcrumbItems: MainBreadcrumbItem[]=[
      {
        href: '',
        key: 'forklift',
        label: 'Przegląd Wózków',
      },
      {
        href: '/forklift/inspections',
        key: 'forklift_inspection',
        label: 'Przegląd Wpisów',
      },
      ...(forklift
        ? [
          {
            href: `/forklift/inspections/${forklift.id}`,
            key: 'forklift_inspection_id',
            label: forklift.number,
          },
        ]
        : []),
    ];



    props.handleSetMainTitle(forklift ? `Przegląd Wpisów - ${forklift.number}` : 'Przegląd Wszystkich Wpisów');
    props.setBreadcrumbHandler(breadcrumbItems);

    selectedMenuContext?.setSelectedKey(['forklift_inspection']);

  },[forklift, linkParams.forkliftId]);

  type DataIndex = keyof ForkliftInspectionInterface;



  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex,
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };


  const getColumnSearchProps = (dataIndex: DataIndexInput): TableColumnType<ForkliftInspectionInterface> => {
    const key = dataIndexKey(dataIndex);

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder="Szukaj"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys as string[], confirm, key as any)}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys as string[], confirm, key as any)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Szukaj
            </Button>
            <Button
              onClick={() => {
                clearFilters && handleReset(clearFilters);
                handleSearch(selectedKeys as string[], confirm, key as any);
                clearFilters && handleReset(clearFilters);
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            <Button type="link" size="small" onClick={() => close()}>
              Zamknij
            </Button>
          </Space>
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),

      onFilter: (value, record) =>
        recordToSearchText(record, dataIndex).includes(String(value).toLowerCase()),

      filterDropdownProps: {
        onOpenChange(open) {
          if (open) setTimeout(() => searchInput.current?.select(), 100);
        },
      },

      // ✅ render powinien bazować na record, nie na "text"
      render: (_text, record) => {
        const cellText = recordToSearchText(record, dataIndex); // ✅ to samo co filtr
        return searchedColumnKey === key ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={cellText || ''}
          />
        ) : (
          cellText
        );
      },
    };
  };



  const columns: ColumnsType<ForkliftInspectionInterface> = [
    {
      title: 'Numer Dokumentu',
      dataIndex: 'inspectionNumber',
      sorter: (a, b) => myStringSorter(a.inspectionNumber, b.inspectionNumber),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('inspectionNumber'),
      render: (value) => <span>{value}</span>
    },

    {
      title: 'Numer Wózka',
      dataIndex: ['forklift', 'number'],
      sorter: (a, b) => myStringSorter(a.forklift.number, b.forklift.number),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps(['forklift', 'number']),
    },


    {
      title: 'Data',
      dataIndex: 'createdAt',
      sorter: (a, b) => myDateSorter(a.createdAt, b.createdAt),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('createdAt'),
    },

    {
      title: 'Prawidłowość działania układu kierowniczego',
      width: 150,
      dataIndex: 'check1',
      sorter: (a, b) => myNumberSorter(a.check1, b.check1),
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        return (
          <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
            {value === 1 ? <CheckOutlined style={{color: 'green'}} /> : <CloseOutlined style={{color: 'red'}} />}
          </div>

        )

      }
    },

    {
      title: 'Prawidłowość działania układu hamulcowego',
      width: 150,
      dataIndex: 'check2',
      sorter: (a, b) => myNumberSorter(a.check2, b.check2),
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        return (
          <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
            {value === 1 ? <CheckOutlined style={{color: 'green'}} /> : <CloseOutlined style={{color: 'red'}} />}
          </div>

        )

      }
    },


    {
      title: 'Prawidłowość działania układu napędowego',
      width: 150,
      dataIndex: 'check3',
      sorter: (a, b) => myNumberSorter(a.check3, b.check3),
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        return (
          <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
            {value === 1 ? <CheckOutlined style={{color: 'green'}} /> : <CloseOutlined style={{color: 'red'}} />}
          </div>

        )

      }
    },

    {
      title: 'Prawidłowość działania układu mechanizmów podnoszenia oraz osprzętu',
      width: 150,
      dataIndex: 'check4',
      sorter: (a, b) => myNumberSorter(a.check4, b.check4),
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        return (
          <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
            {value === 1 ? <CheckOutlined style={{color: 'green'}} /> : <CloseOutlined style={{color: 'red'}} />}
          </div>

        )

      }
    },

    {
      title: 'Sprawność oświetlenia i sygnalizacji dźwiękowej',

      dataIndex: 'check5',
      sorter: (a, b) => myNumberSorter(a.check5, b.check5),
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        return (
          <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
            {value === 1 ? <CheckOutlined style={{color: 'green'}} /> : <CloseOutlined style={{color: 'red'}} />}
          </div>

        )

      }
    },

    {
      title: 'Prawidłowość działania urządzeń pomiarowo kontrolnych',
      width: 150,
      dataIndex: 'check6',
      sorter: (a, b) => myNumberSorter(a.check6, b.check6),
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        return (
          <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
            {value === 1 ? <CheckOutlined style={{color: 'green'}} /> : <CloseOutlined style={{color: 'red'}} />}
          </div>

        )

      }
    },


    {
      title: 'Prawidłowość działania elementów i urządzeń zabezpieczających kierowcę i osoby współuczestniczące w czynnościach transportowych (kabina, osłona)',

      dataIndex: 'check7',
      width: 150,
      sorter: (a, b) => myNumberSorter(a.check7, b.check7),
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        return (
          <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
            {value === 1 ? <CheckOutlined style={{color: 'green'}} /> : <CloseOutlined style={{color: 'red'}} />}
          </div>

        )

      }
    },


    {
      title: 'Komentarz',
      minWidth: 150,
      dataIndex: 'comment',
      sorter: (a, b) => myStringSorter(a.comment, b.comment),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('comment'),
      render: (value, record) => {
        const comment = record.comment;
        const wrappedComment = comment && comment.length > 45
          ? comment.match(/.{1,45}/g)?.map((part, index) => <div key={index}>{part}</div>)
          : comment;
        return <div>{wrappedComment}</div>;
      },
    },

    {
      title: 'Dodatkowy Komentarz',
      minWidth: 150,
      dataIndex: 'additionalComment',
      sorter: (a, b) => myStringSorter(a.additionalComment, b.additionalComment),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('additionalComment'),
      render: (value, record) => {
        const comment = record.additionalComment;
        const wrappedComment = comment && comment.length > 45
          ? comment.match(/.{1,45}/g)?.map((part, index) => <div key={index}>{part}</div>)
          : comment;
        return <div>{wrappedComment}</div>;
      },
    },

    {
      title: 'Imię i Nazwisko',
      minWidth: 150,
      dataIndex: 'fullName',
      sorter: (a, b) => myStringSorter(a.fullName, b.fullName),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('fullName'),
      render: (value) => <span>{value}</span>

    },

    {
      title: 'Akcje',
      key: 'action',
      minWidth: 100,
      dataIndex: '',
      fixed: 'end',
      render: (_value, record) => <><a href={`${API_URL}forklift/inspection/pdf/${record.id}`}><Button type={'text'} icon={<FileDown />}></Button></a></>
    }


  ];

/*

  const downloadFile = (inspectionId: string) => {
    const url = `${API_URL}forklift/inspection/pdf/${inspectionId}`;
    const link = document.createElement('a');
    link.href = url;
   // link.target = '_blank';
    link.rel = 'noreferrer';
    link.click();
  }
*/



  const handleChangeDate = (date: any) => {
  setCurrentRange(date);
  }

  if(permError) {
    return <PermError/>
  }



  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: ForkliftInspectionInterface[]) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
      setTableSelected(selectedRows);
      setSelectedRowKeys(selectedRowKeys);
    },
    /*    getCheckboxProps: (record: DataType) => ({
          disabled: record.kod_elica === 'PRF123', // Column configuration not to be checked
          name: record.kod_elica,
        }),*/
  };


  const handleExcelClick = (data: ForkliftInspectionInterface[]) => {
    const dataToExport = data.map((data: ForkliftInspectionInterface,index) => {
      return {
        'Numer Dokumentu': data.inspectionNumber,
        'Numer Wózka': data.forklift.number,
        Data: (data as any).dateForExcel,
        'Prawidłowość działania układu kierowniczego': data.check1 ? 'SPRAWNY' : 'NIESPRAWNY',
        'Prawidłowość działania układu hamulcowego': data.check2 ? 'SPRAWNY' : 'NIESPRAWNY',
        'Prawidłowość działania układu napędowego': data.check3 ? 'SPRAWNY' : 'NIESPRAWNY',
        'Prawidłowość działania układu mechanizmów podnoszenia oraz osprzętu': data.check4 ? 'SPRAWNY' : 'NIESPRAWNY',
        'Sprawność oświetlenia i sygnalizacji dźwiękowej': data.check5 ? 'SPRAWNY' : 'NIESPRAWNY',
        'Prawidłowość działania urządzeń pomiarowo kontrolnych': data.check6 ? 'SPRAWNY' : 'NIESPRAWNY',
        'Prawidłowość działania elementów i urządzeń zabezpieczających kierowcę i osoby współuczestniczące w czynnościach transportowych (kabina, osłona)': data.check7 ? 'SPRAWNY' : 'NIESPRAWNY',
        Komentarz: data.comment,
        'Dodatkowy Komentarz': data.additionalComment,
        'Imię i Nazwisko': data.fullName,
      }
    })
    exportToExcel(dataToExport);
  };


  return (
    <>
      <Table
        rowSelection={{
          selectedRowKeys,
          type: 'checkbox',
          fixed: 'start',
          ...rowSelection,
        }}
        rowKey={'id'}
        footer={() => <TableFooter selectedCount={tableSelected.length} totalCount={tableData.length} />}
        title={(records) => <TableHeader handleExcelExportSelectedClick={() => handleExcelClick(tableSelected)} handleExcelExportAllClick={() => handleExcelClick(tableData)} getData={getData} selectedCount={tableSelected.length} additional={<><DatePicker.RangePicker format={'DD/MM/YYYY'} value={currentRange} onChange={handleChangeDate} defaultValue={currentRange} /></>} /> } columns={columns} scroll={{x: 'max-content', y: windowHeight *.6}} pagination={false} loading={loading} showHeader={true} bordered={true} dataSource={tableData} style={{opacity: 0.9}} size={'small'} />
    </>

  )






}
